(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motions = new Set();
  const animate = (element, frames, options) => {
    if (!element?.animate || reduced.matches) return;
    const motion = element.animate(frames, { duration: 480, easing: 'cubic-bezier(.22,1,.36,1)', ...options });
    motions.add(motion);
    motion.finished.then(() => motions.delete(motion), () => motions.delete(motion));
  };
  reduced.addEventListener('change', () => {
    if (reduced.matches) motions.forEach((motion) => motion.cancel());
  });

  if (!customElements.get('hydros-lab-comparison')) {
    customElements.define('hydros-lab-comparison', class extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('input[type="range"]');
        if (!this.range) return;
        this.events?.abort();
        this.events = new AbortController();
        this.update = () => {
          const value = Math.min(100, Math.max(0, Number(this.range.value)));
          this.style.setProperty('--position', `${value}%`);
        };
        this.range.addEventListener('input', this.update, { signal: this.events.signal });
        this.range.hidden = false;
        this.querySelector('.hydros-lab-compare__hint').hidden = false;
        this.classList.add('is-ready');
        this.update();
      }
      disconnectedCallback() { this.events?.abort(); }
    });
  }

  if (!customElements.get('hydros-product-stories')) {
    customElements.define('hydros-product-stories', class extends HTMLElement {
      connectedCallback() {
        this.events?.abort();
        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.dialog = this.querySelector('dialog');
        this.player = this.querySelector('video');
        if (!this.dialog || !this.player) return;
        const close = this.querySelector('[data-hydros-ugc-close]');
        const title = this.querySelector('[data-hydros-ugc-title]');
        const note = this.querySelector('[data-hydros-ugc-note]');
        this.querySelectorAll('[data-hydros-ugc-open]').forEach((button) => {
          button.addEventListener('click', () => {
            this.opener = button;
            document.querySelectorAll('video').forEach((video) => { if (video !== this.player) video.pause(); });
            this.player.src = button.dataset.videoSrc;
            this.player.poster = button.dataset.videoPoster || '';
            this.player.setAttribute('data-format', button.dataset.videoCrop || button.dataset.videoFormat || 'portrait');
            title.textContent = button.dataset.videoTitle || '';
            note.textContent = button.dataset.videoNote || '';
            note.hidden = !button.dataset.videoNote;
            this.dialog.showModal();
            this.player.play().catch(() => {});
            if (button.matches(':focus-visible')) close.focus({ preventScroll: true });
            else animate(this.dialog, [{ opacity: .3, transform: 'translateY(14px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 240 });
          }, options);
        });
        close.addEventListener('click', () => this.dialog.close(), options);
        this.dialog.addEventListener('click', (event) => { if (event.target === this.dialog) this.dialog.close(); }, options);
        this.dialog.addEventListener('close', () => {
          this.clearPlayer();
          if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
        }, options);
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.player.pause(); }, options);
      }
      clearPlayer() {
        this.player?.pause();
        this.player?.removeAttribute('src');
        this.player?.load();
      }
      disconnectedCallback() {
        this.events?.abort();
        if (this.dialog?.open) this.dialog.close();
        this.clearPlayer();
      }
    });
  }

  const states = new WeakMap();
  const init = (root = document) => {
    const pages = [...root.querySelectorAll('.hydros-pdp')];
    if (root.matches?.('.hydros-pdp')) pages.push(root);
    pages.forEach((page) => {
      if (states.has(page)) return;
      const events = new AbortController();
      let observer;
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          animate(entry.target, [{ opacity: .45, transform: 'translateY(18px)' }, { opacity: 1, transform: 'translateY(0)' }]);
          observer.unobserve(entry.target);
        }), { threshold: .12 });
        page.querySelectorAll('.hydros-pdp__life-copy,.hydros-pdp__mechanism > header,.hydros-pdp__mechanism-grid > figure,.hydros-pdp__lab header,.hydros-pdp__reviews').forEach((element) => observer.observe(element));
      }
      const gallery = page.querySelector('.hydros-pdp__gallery');
      if (gallery) {
        const stage = gallery.querySelector('.hydros-pdp__stage');
        let selected = gallery.querySelector('.is-active');
        let pendingImage;
        // Bubble after the product controller has applied the new media source.
        gallery.addEventListener('click', (event) => {
            const thumb = event.target.closest('[data-hynox-gallery-thumb],[data-lumos-gallery-thumb]');
            if (!thumb || !gallery.contains(thumb) || thumb === selected) return;
            selected = thumb;
            pendingImage?.abort();
            if (event.detail === 0 || reduced.matches || !stage) return;
            const main = gallery.querySelector('.hynox-gallery__main,.lumos-gallery__main');
            const reveal = () => {
              motions.forEach((motion) => { if (motion.effect?.target === stage) motion.cancel(); });
              animate(stage, [{ opacity: .55 }, { opacity: 1 }], { duration: 280 });
            };
            if (main && !main.classList.contains('is-hidden') && !main.complete) {
              pendingImage = new AbortController();
              main.addEventListener('load', reveal, { once: true, signal: pendingImage.signal });
            } else reveal();
        }, { signal: events.signal });
        events.signal.addEventListener('abort', () => pendingImage?.abort(), { once: true });
      }
      page.querySelectorAll('details').forEach((details) => {
        let keyboard = false;
        details.querySelector('summary')?.addEventListener('click', (event) => { keyboard = event.detail === 0; }, { signal: events.signal });
        details.addEventListener('toggle', () => {
          if (!details.open || keyboard) return;
          [...details.children].filter((el) => el.tagName !== 'SUMMARY').forEach((el) => animate(el, [{ opacity: .4 }, { opacity: 1 }], { duration: 180 }));
        }, { signal: events.signal });
      });
      states.set(page, { observer, events });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.hydros-pdp').forEach((page) => {
      const state = states.get(page);
      state?.observer?.disconnect();
      state?.events.abort();
      motions.forEach((motion) => { if (page.contains(motion.effect?.target)) motion.cancel(); });
      states.delete(page);
    });
  });
})();
